import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";

import Box from "components/common/Box";
import Button from "components/common/Button";
import Grid from "components/common/Grid";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Modal from "components/common/Modal";
import ScheduleCallModal from "components/common/ScheduleCallModal";
import { CircularProgress } from "@mui/material";
import { utils } from "helper/utils";
import LiquiditySynopsisSection from "./PHCReportCompount/LiquiditySynopsisSection";
import PerformanceChart from "./Graph/PerformanceChat";
import PerformanceAnalytics from "./PHCReportCompount/PerformanceAnalytics";
import StockAllocationTable from "./PHCReportCompount/StockAllocationTable";
import TaxEfficiencySection from "./PHCReportCompount/TaxEfficiencySection";
import JarvisAnalyticsTable from "./PHCReportCompount/JarvisAnalyticsTable";
import ParameterRating from "./PHCReportCompount/ParameterRating";
import PortfolioSnapshot from "./PHCReportCompount/PortfolioSnapshot";
import PhcReportHeader, {
  SaveReportBar,
} from "./PHCReportCompount/PhcReportHeader";
import PortfolioMetrics from "./PHCReportCompount/PortfolioMetrics";
import SectorDiversificationTable from "./PHCReportCompount/SectorDiversificationTable";
import PortfolioHighlights from "./PHCReportCompount/PortfolioHighlights";
import TopPerformance from "./Graph/TopPerformance";
import RatioGlossary from "./PHCReportCompount/RatioGlossary";
import RecommendedProductsSection from "./PHCReportCompount/RecommendedProductsSection";
import { PhcThemeProvider, usePhcTheme } from "./PhcThemeContext";
import { dateTimeHelper } from "helper/dateTime";

import Separator from "components/common/Separator";
import { useSelector } from "react-redux";
import { PHCOnboardingApis } from "stores/PHC/PHCOnboarding/PHCOnboardingApis";

// Create memoized components to prevent unnecessary re-renders
const MemoizedStockAllocationTable = React.memo(StockAllocationTable);
const MemoizedParameterRating = React.memo(ParameterRating);
const MemoizedJarvisAnalyticsTable = React.memo(JarvisAnalyticsTable);

const formatSnapshotCurrency = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) {
    return "-";
  }

  return `₹${Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const isAbove25LacFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

function GraphDashboardContent({
  openModal,
  portfolioId,
  isShortUrl,
  shouldRegenerate,
}) {
  const { theme, mode } = usePhcTheme();

  // Combine all data into single state to reduce re-renders
  const [allData, setAllData] = useState({
    graphData: {
      initialCalculations: {},
      portfolioMetrics: [],
      sectorDiversification: [],
      topPerformers: [],
      topLosers: [],
      liquiditySynopsis: null,
      portfolioData: [],
      benchmarkData: [],
      extraBenchmarks: {},
      footerReturns: {},
    },
    stockAllocationData: [],
    jarvisAnalyticsData: [],
    sectorAnalyticsData: [],
    highlightsData: null,
    taxEfficiencyData: null,
    performanceAnalyticsData: null,
    parameterRatingData: null,
    sectorDiversificationApi: null,
    sectorHighlights: null,
    liquidityAnalysisData: null,
    recommendProduct: null,
    portfolioReview: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [openRMModal, setOpenRMModal] = useState(false);

  // Read via ref so saving the open report (updatedAt null -> date) doesn't
  // re-run the fetch and trigger a regenerate right after save.
  const shouldRegenerateRef = useRef(shouldRegenerate);
  shouldRegenerateRef.current = shouldRegenerate;

  const { CKYCDone, affiliationId, isAccountHolder } = useSelector(
    (state) => state.PHC.PHCOnboarding,
  );
  const { isCkycDone } = useSelector((state) => state.common);

  
  const { tags } = useSelector((state) => state.PHC.PHCDashboard);

  // Destructure data after state
  const {
    graphData,
    stockAllocationData,
    jarvisAnalyticsData,
    sectorAnalyticsData,
    highlightsData,
    taxEfficiencyData,
    performanceAnalyticsData,
    parameterRatingData,
    sectorDiversificationApi,
    sectorHighlights,
    liquidityAnalysisData,
    recommendProduct,
    portfolioReview,
  } = allData;

  // Single useEffect with combined data fetching
  useEffect(() => {
    if (!portfolioId) return;

    let cancelled = false;

    const fetchAllData = async () => {
      setIsLoading(true);
      setLoadError(null);
      setIsRegenerating(Boolean(shouldRegenerateRef.current));

      // Saved report: regenerate first, load only if it succeeds
      if (shouldRegenerateRef.current) {
        let regenerateRes = null;
        let regenerateError = null;
        try {
          regenerateRes =
            await PHCOnboardingApis.regenerateReport(portfolioId);
        } catch (error) {
          regenerateError = error;
        }

        if (cancelled) return;
        setIsRegenerating(false);

        if (regenerateRes?.success !== true) {
          setLoadError(
            regenerateRes?.message ||
              regenerateError?.response?.data?.message ||
              "We couldn't refresh your report. Please try again.",
          );
          setIsLoading(false);
          return;
        }
      }

      try {
        // Phase 1 + Phase 2 APIs in parallel; new endpoints fail soft
        const [
          graphRes,
          performanceRes,
          sjsRes,
          prlpRes,
          highlightsRes,
          sectorRes,
          liquidityRes,
          perfAnalyticsRes,
          recommendRes,
          aiRecommendRes,
        ] = await Promise.all([
          PHCOnboardingApis.getGraphData(portfolioId),
          PHCOnboardingApis.getGrapDataPerforamnceChart(portfolioId),
          PHCOnboardingApis.getGrapDataSJS(portfolioId),
          PHCOnboardingApis.getGrapDataPRLP(portfolioId),
          PHCOnboardingApis.getPortfolioHighlights(portfolioId).catch(
            () => null,
          ),
          PHCOnboardingApis.getSectorDiversification(portfolioId).catch(
            () => null,
          ),
          PHCOnboardingApis.getLiquidityAnalysis(portfolioId).catch(
            () => null,
          ),
          PHCOnboardingApis.getPerformanceAnalytics(portfolioId).catch(
            () => null,
          ),
          PHCOnboardingApis.getRecommendProduct(portfolioId).catch(
            () => null,
          ),
          PHCOnboardingApis.getAiRecommend(portfolioId).catch(
            () => null,
          ),
        ]);

        const graphResult = graphRes?.result?.[0] || {};
        const formattedMetrics = utils.formatMetrics(
          graphResult.portfolioMetrics,
        );

        // performance-chart: new shape uses result.chart_data + result.summary
        // keep fallback to old result[0].portfolioData / niftyData
        const performanceResult = performanceRes?.result;
        const chartData =
          performanceResult?.chart_data ||
          performanceResult?.[0]?.chart_data ||
          null;
        const performanceSummary =
          performanceResult?.summary ||
          performanceResult?.[0]?.summary ||
          {};
        const legacyPerformance = performanceResult?.[0] || {};

        const sjsResult = sjsRes?.result?.[0] || {};

        // portfolio-profiling: new shape is result object; keep old data/result[0] fallbacks
        const profilingResult =
          (prlpRes?.result &&
            !Array.isArray(prlpRes.result) &&
            (prlpRes.result.portfolio_profiling ||
              prlpRes.result.risk ||
              prlpRes.result.liquidity ||
              prlpRes.result.performance) &&
            prlpRes.result) ||
          prlpRes?.data ||
          prlpRes?.result?.[0] ||
          null;

        // Phase 2 — portfolio_highlights
        const highlightsPayload =
          highlightsRes?.result?.[0] ||
          (Array.isArray(highlightsRes?.result)
            ? null
            : highlightsRes?.result) ||
          graphResult.highlights ||
          graphResult.portfolioHighlights ||
          sjsResult.highlights ||
          sjsResult.portfolioHighlights ||
          null;

        // Phase 2 — sector_diversification
        const sectorResult =
          (!Array.isArray(sectorRes?.result) && sectorRes?.result) ||
          sectorRes?.result?.[0] ||
          null;
        const sectorRowsFromApi = Array.isArray(
          sectorResult?.sector_diversification,
        )
          ? sectorResult.sector_diversification
          : Array.isArray(sectorResult?.sectorDiversification)
            ? sectorResult.sectorDiversification
            : null;
        const sectorHighlightsFromApi = Array.isArray(
          sectorResult?.sector_highlights,
        )
          ? sectorResult.sector_highlights
          : Array.isArray(sectorResult?.sectorHighlights)
            ? sectorResult.sectorHighlights
            : null;

        // Phase 2 — liquidity_analysis
        const liquidityPayload =
          (!Array.isArray(liquidityRes?.result) && liquidityRes?.result) ||
          liquidityRes?.result?.[0] ||
          null;

        // Phase 2 — performance_analytics
        const perfAnalyticsPayload =
          (!Array.isArray(perfAnalyticsRes?.result) &&
            perfAnalyticsRes?.result) ||
          perfAnalyticsRes?.result?.[0] ||
          graphResult.performanceAnalytics ||
          graphResult.performance_analytics ||
          sjsResult.performanceAnalytics ||
          sjsResult.performance_analytics ||
          null;

        const recommendPayload =
          (recommendRes?.data &&
            typeof recommendRes.data === "object" &&
            !Array.isArray(recommendRes.data) &&
            recommendRes.data) ||
          (!Array.isArray(recommendRes?.result) && recommendRes?.result) ||
          recommendRes?.result?.[0] ||
          null;

        const aiRecommendPayload =
          (aiRecommendRes?.data &&
            typeof aiRecommendRes.data === "object" &&
            !Array.isArray(aiRecommendRes.data) &&
            aiRecommendRes.data) ||
          (!Array.isArray(aiRecommendRes?.result) && aiRecommendRes?.result) ||
          aiRecommendRes?.result?.[0] ||
          null;

        if (cancelled) return;

        // SINGLE STATE UPDATE - reduces re-renders
        setAllData({
          graphData: {
            initialCalculations: graphResult.initialCalculations || {},
            portfolioMetrics: formattedMetrics,
            sectorDiversification: utils.formatChartData(
              graphResult.sectorDiversification,
            ),
            topPerformers: utils.formatChartData(graphResult.topPerformers),
            topLosers: utils.formatChartData(graphResult.topLosers),
            liquiditySynopsis: graphResult.liquiditySynopsis,
            portfolioData:
              chartData?.portfolio_returns ||
              legacyPerformance.portfolioData ||
              [],
            benchmarkData:
              chartData?.nifty_50 ||
              legacyPerformance.niftyData ||
              [],
            extraBenchmarks: {
              nifty500:
                chartData?.nifty_500 ||
                legacyPerformance.nifty500Data ||
                [],
              niftyMidcap:
                chartData?.nifty_midcap ||
                legacyPerformance.niftyMidcapData ||
                [],
              niftySmallcap:
                chartData?.nifty_smallcap ||
                legacyPerformance.niftySmallcapData ||
                [],
            },
            footerReturns: {
              portfolio:
                performanceSummary.portfolio_returns ??
                performanceSummary.portfolio,
              nifty50:
                performanceSummary.nifty_50 ?? performanceSummary.nifty50,
              nifty500:
                performanceSummary.nifty_500 ?? performanceSummary.nifty500,
              niftyMidcap:
                performanceSummary.nifty_midcap ??
                performanceSummary.niftyMidcap,
              niftySmallcap:
                performanceSummary.nifty_smallcap ??
                performanceSummary.niftySmallcap,
            },
          },
          stockAllocationData: sjsResult.stockAllocation || [],
          jarvisAnalyticsData: sjsResult.jarvisAnalytics || [],
          sectorAnalyticsData: sjsResult.sectorAnalytics || [],
          highlightsData: highlightsPayload,
          taxEfficiencyData:
            graphResult.taxEfficiency ||
            graphResult.ltcgProfile ||
            sjsResult.taxEfficiency ||
            sjsResult.ltcgProfile ||
            null,
          performanceAnalyticsData: perfAnalyticsPayload,
          parameterRatingData: profilingResult,
          sectorDiversificationApi: sectorRowsFromApi,
          sectorHighlights: sectorHighlightsFromApi,
          liquidityAnalysisData: liquidityPayload,
          recommendProduct: recommendPayload,
          portfolioReview: aiRecommendPayload,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAllData();

    return () => {
      cancelled = true;
    };
  }, [portfolioId, reloadKey]);

  // Portfolio Snapshot cards — same API fields, dark Figma UI
  const snapshotItems = useMemo(() => {
    const returnsRaw = graphData.initialCalculations.returns;
    const returnsNum =
      returnsRaw != null ? Number(String(returnsRaw).replace("%", "")) : null;

    return [
      {
        key: "invested",
        themeKey: "invested",
        value: formatSnapshotCurrency(
          graphData.initialCalculations.investment_amount
        ),
        label: "Invested Value",
      },
      {
        key: "current",
        themeKey: "current",
        value: formatSnapshotCurrency(
          graphData.initialCalculations.current_value
        ),
        label: "Current Value",
      },
      {
        key: "returns",
        themeKey: "returns",
        value: returnsRaw != null ? `${returnsRaw}%` : "-",
        label: "Returns",
        valueColor:
          returnsNum == null
            ? theme.textPrimary
            : returnsNum < 0
              ? theme.negative
              : theme.positive,
      },
      {
        key: "stocks",
        themeKey: "stocks",
        value: graphData.initialCalculations.stock_count ?? "-",
        label: "Number of Stocks",
      },
      {
        key: "beta",
        themeKey: "beta",
        value: graphData.initialCalculations.beta ?? "-",
        label: "Portfolio Beta",
      },
    ];
  }, [
    graphData.initialCalculations.investment_amount,
    graphData.initialCalculations.current_value,
    graphData.initialCalculations.returns,
    graphData.initialCalculations.stock_count,
    graphData.initialCalculations.beta,
    theme,
  ]);

  const handleSaveReport = () => {
    openModal();
  };

  const handleSpeakToRm = useCallback(() => {
    setOpenRMModal(true);
  }, []);

  const closeRMModal = useCallback(() => {
    setOpenRMModal(false);
  }, []);

  // Header meta — portfolio name + report date from API / tags
  const activePortfolio = useMemo(
    () => tags?.find((t) => t.id === portfolioId || t.isActive),
    [tags, portfolioId],
  );

  const reportPortfolioName =
    activePortfolio?.name ||
    activePortfolio?.portfolioData?.portfolioName ||
    "-";

  // Report date — buy_date only (Figma / product requirement)
  const reportDateRaw = graphData.initialCalculations?.created_at;

  const reportDateDisplay = reportDateRaw
    ? dateTimeHelper.format(reportDateRaw, "$D $MMMM $YYYY")
    : "-";

  // Sector Diversification table rows — prefer new API; else merge weight + analytics
  const sectorDiversificationRows = useMemo(() => {
    const normalize = (value = "") =>
      String(value).trim().toLowerCase().replace(/\s+/g, " ");

    const stocksBySector = new Map();
    (stockAllocationData || []).forEach((stock) => {
      const key = normalize(stock.sector);
      if (!key) return;
      if (!stocksBySector.has(key)) stocksBySector.set(key, []);
      stocksBySector.get(key).push(stock);
    });

    const findMatch = (sectorName, map) => {
      const key = normalize(sectorName);
      if (map.has(key)) return map.get(key);
      for (const [mapKey, value] of map.entries()) {
        if (mapKey.includes(key) || key.includes(mapKey)) return value;
      }
      return null;
    };

    // Phase 2 API — full rows already shaped for the table
    if (
      Array.isArray(sectorDiversificationApi) &&
      sectorDiversificationApi.length > 0
    ) {
      return sectorDiversificationApi.map((row) => {
        const sectorName = row.sector || row.name || "";
        return {
          sector: sectorName,
          holdings: row.holdings ?? row.stock_count ?? row.holdings_count ?? null,
          value: row.value != null ? Number(row.value) : null,
          investedAmount:
            row.invested_amount != null
              ? Number(row.invested_amount)
              : row.investedAmount != null
                ? Number(row.investedAmount)
                : null,
          returns: row.returns ?? null,
          weight: row.weight ?? row.weightage ?? null,
          remark: row.remark || null,
          stocks: findMatch(sectorName, stocksBySector) || [],
        };
      });
    }

    // Prefer SJS sectorAnalytics when it already has full row shape
    if (Array.isArray(sectorAnalyticsData) && sectorAnalyticsData.length > 0) {
      const first = sectorAnalyticsData[0];
      if (
        first &&
        (first.sector || first.sector_name) &&
        (first.value != null || first.current_value != null || first.weight != null)
      ) {
        return sectorAnalyticsData.map((row) => {
          const sectorName = row.sector || row.sector_name || row.name || "";
          return {
            sector: sectorName,
            holdings: row.holdings ?? row.stock_count ?? row.holdings_count ?? null,
            value:
              row.value != null
                ? Number(row.value)
                : row.current_value != null
                  ? Number(row.current_value)
                  : null,
            investedAmount:
              row.invested_amount != null
                ? Number(row.invested_amount)
                : row.investedAmount != null
                  ? Number(row.investedAmount)
                  : null,
            returns: row.returns ?? null,
            weight: row.weight ?? row.weightage ?? null,
            remark: row.remark || null,
            stocks: findMatch(sectorName, stocksBySector) || [],
          };
        });
      }
    }

    const analyticsBySector = new Map();
    (sectorAnalyticsData || []).forEach((item) => {
      const key = normalize(item.sector_name || item.sector || item.name);
      if (key) analyticsBySector.set(key, item);
    });

    const pickHoldings = (analytics) => {
      if (!analytics) return null;
      if (analytics.holdings != null && analytics.holdings !== "") {
        return analytics.holdings;
      }
      if (analytics.stock_count != null && analytics.stock_count !== "") {
        return analytics.stock_count;
      }
      if (analytics.holdings_count != null && analytics.holdings_count !== "") {
        return analytics.holdings_count;
      }
      return null;
    };

    return (graphData.sectorDiversification || []).map((item) => {
      const analytics = findMatch(item.name, analyticsBySector);
      const stocks = findMatch(item.name, stocksBySector) || [];

      return {
        sector: item.name,
        weight: item.y,
        holdings: pickHoldings(analytics),
        value:
          analytics?.value != null
            ? Number(analytics.value)
            : analytics?.current_value != null
              ? Number(analytics.current_value)
              : null,
        remark: analytics?.remark || null,
        stocks,
      };
    });
  }, [
    sectorDiversificationApi,
    graphData.sectorDiversification,
    sectorAnalyticsData,
    stockAllocationData,
  ]);

  // Fixed logic: CKYCDone 0 = locked, not onboarded = blur
  const getComponentState = useCallback(
    (specialCase = false) => {
      const isCKYCComplete = CKYCDone == 1 || isCkycDone == true;

      // CKYC done = normal
      if (isCKYCComplete || isShortUrl) {
        return "normal";
      }

      if (
        (isAccountHolder === 0 || isAccountHolder === 1) &&
        affiliationId !== null &&
        affiliationId > 1
      ) {
        return "blur";
      }

      // All other cases when CKYC not done = locked
      return "locked";
    },
    [CKYCDone, isCkycDone, isAccountHolder, affiliationId, isShortUrl],
  );

  // Memoize component state
  const componentState = useMemo(
    () => getComponentState(),
    [getComponentState],
  );

  // Early return for loading state
  if (isLoading && portfolioId) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          {!isRegenerating && <CircularProgress />}
        </Box>

        <Modal
          open={isRegenerating}
          disableClose={true}
          disableEscapeKeyDown={true}
          size="xs"
        >
          <Stack alignItems="center" spacing={2} sx={{ py: 2, textAlign: "center" }}>
            <CircularProgress size={36} />
            <Text sx={{ color: "#222B3E", fontWeight: 600, fontSize: "18px" }}>
              Regenerating your report
            </Text>
            <Text sx={{ color: "#6B7280", fontSize: "14px" }}>
              We're refreshing your Portfolio Health Check with the latest
              data. This may take some time, please don't close or refresh
              this page.
            </Text>
          </Stack>
        </Modal>
      </>
    );
  }

  if (loadError && portfolioId) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          px: 2,
          bgcolor: theme.pageBg,
        }}
      >
        <Stack alignItems="center" spacing={2} sx={{ maxWidth: 420 }}>
          <Text
            variant="body1"
            sx={{ color: theme.textPrimary, textAlign: "center" }}
          >
            {loadError}
          </Text>
          <Button onClick={() => setReloadKey((key) => key + 1)}>Retry</Button>
        </Stack>
      </Box>
    );
  }

  const shouldHideButton = tags?.some(
    (item) =>
      item?.portfolioData?.portfolioId === portfolioId &&
      item?.portfolioData?.updatedAt,
  );

  const canShowSaveReport =
    (isAccountHolder || CKYCDone == 1) && !shouldHideButton;

  return (
    <Box
      sx={{
        overflowX: "hidden",
        width: "100%",
        bgcolor: theme.pageBg,
        color: theme.textPrimary,
        borderRadius: { xs: 0, md: 0 },
        px: { xs: "24px", sm: 3, md: 5, lg: 7.5 },
        py: { xs: 2, md: 3 },
        pb: {
          xs: 3,
          md: 3,
        },
        minHeight: "100%",
        m: 0,
      }}
    >
      {/* Report header + trust bar — desktop 5917:2947 / mobile 5626:88598 */}
      <PhcReportHeader
        reportDate={reportDateDisplay}
        portfolioName={reportPortfolioName}
        onSaveReport={canShowSaveReport ? handleSaveReport : undefined}
      />

      {/* Portfolio Snapshot — Figma 5917:2992 */}
      <Box sx={{ mt: { xs: 2.5, md: 4 }, width: "100%" }}>
        <PortfolioSnapshot items={snapshotItems} />
      </Box>

      {/* Portfolio Metrics — Figma 5626:87039 */}
      {graphData.portfolioMetrics.length > 0 && (
        <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
          <PortfolioMetrics metrics={graphData.portfolioMetrics} />
        </Box>
      )}

      {/* Highlights — Figma 5917:3120 */}
      {highlightsData && (
        <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
          <PortfolioHighlights data={highlightsData} />
        </Box>
      )}

      {/* Sector Diversification — Figma 5626:87209 (table + highlight) */}
      {sectorDiversificationRows.length > 0 && (
        <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
          <SectorDiversificationTable
            sectors={sectorDiversificationRows}
            highlights={sectorHighlights}
          />
        </Box>
      )}

      <Separator size={5} />

      {/* Top Losers vs Top Gainers — Figma 5917:3283 */}
      {(graphData.topPerformers.length > 0 ||
        graphData.topLosers.length > 0) && (
        <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
          <TopPerformance
            topPerformersGraphData={graphData.topPerformers}
            topLosersGraphData={graphData.topLosers}
            isLoading={isLoading}
          />
        </Box>
      )}

      <Separator size={5} />

      {/* Liquidity — Figma 5917:3359 (cards + insight + table) */}
      {(liquidityAnalysisData ||
        graphData.liquiditySynopsis ||
        stockAllocationData.length > 0) && (
        <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
          <LiquiditySynopsisSection
            liquidityData={graphData.liquiditySynopsis}
            stockAllocation={stockAllocationData}
            apiSection={liquidityAnalysisData}
          />
        </Box>
      )}

      <Separator size={5} />

      {/* Performance Analytics — Figma 5626:87478 (above chart) */}
      {performanceAnalyticsData && (
        <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
          <PerformanceAnalytics data={performanceAnalyticsData} />
        </Box>
      )}

      <Separator size={5} />

      {/* Performance Chart + mobile returns grid (Figma 5917:5768 / 5917:5808) */}
      {graphData?.portfolioData?.length > 0 && (
        <Box sx={{ mt: { xs: 2.5, md: 0 }, width: "100%" }}>
          <PerformanceChart
            portfolioData={graphData.portfolioData}
            benchmarkData={graphData.benchmarkData}
            extraBenchmarks={graphData.extraBenchmarks}
            footerReturns={graphData.footerReturns}
          />
        </Box>
      )}

      <Separator size={5} />

      {stockAllocationData.length > 0 && (
        <MemoizedStockAllocationTable
          stockAllocation={stockAllocationData}
          isLoading={false}
          componentState={componentState}
        />
      )}

      <Separator size={5} />

      {/* Tax Efficiency / Dividend / Additional metrics */}
      {taxEfficiencyData && (
        <Box sx={{ mt: { xs: 2, md: 3 } }}>
          <TaxEfficiencySection data={taxEfficiencyData} />
        </Box>
      )}

      <Separator size={5} />

      {parameterRatingData && Object.keys(parameterRatingData).length > 0 && (
        <MemoizedParameterRating
          data={parameterRatingData}
          isLoading={isLoading}
          componentState={componentState}
        />
      )}

      <Separator size={5} />

      {jarvisAnalyticsData.length > 0 && (
        <MemoizedJarvisAnalyticsTable
          javistable={jarvisAnalyticsData}
          isLoading={false}
          componentState={componentState}
        />
      )}

      <Separator size={5} />

      {/* Recommended Product CTA — Figma 5895:3624 (above glossary) */}
      <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
        <RecommendedProductsSection
          portfolioValue={
            recommendProduct?.current_value ??
            graphData.initialCalculations?.current_value
          }
          estimatedSellCalls={recommendProduct?.total_sell_value}
          isAbove25Lac={isAbove25LacFlag(recommendProduct?.is_above_25_lac)}
          review={portfolioReview}
          onSpeakToRm={handleSpeakToRm}
          componentState={componentState}
        />
      </Box>

      <Separator size={5} />

      {/* Ratio Glossary — Figma 5626:88418 */}
      <Box sx={{ mt: { xs: 2.5, md: 3 }, width: "100%" }}>
        <RatioGlossary
          metrics={graphData.portfolioMetrics}
          beta={graphData.initialCalculations?.beta}
          liquiditySynopsis={graphData.liquiditySynopsis}
          stockCount={graphData.initialCalculations?.stock_count}
        />
      </Box>

      {/* Save Report — bottom of report, Light and Dark */}
      {canShowSaveReport && (
        <SaveReportBar onSaveReport={handleSaveReport} />
      )}

      <ScheduleCallModal
        open={openRMModal}
        onClose={closeRMModal}
        leadOrigin="phc"
        mode={mode}
      />
    </Box>
  );
}

export default function GraphDashboard(props) {
  return (
    <PhcThemeProvider>
      <GraphDashboardContent {...props} />
    </PhcThemeProvider>
  );
}
